import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Icon } from '../icons';
import HelpTip from './HelpTip';
import Select from './Select';
import SearchInput from './SearchInput';
import Pagination, { usePagination } from './Pagination';
import SortableTh, { useSort, sortRows } from './SortableTh';
import Drawer from './Drawer';
import { useAsyncAction } from '../hooks/useAsyncAction';
import { useConfirm } from './ConfirmProvider';

const api = (path, opts = {}) =>
  fetch(path, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...opts });

// Stessa terminologia IT del filtro "Tutti gli stati" qui sotto.
const CODE_STATUS = {
  unused: { label: 'Non usato', cls: 'badge-info' },
  used: { label: 'Usato', cls: 'badge-muted' },
  revoked: { label: 'Revocato', cls: 'badge-danger' },
  expired: { label: 'Scaduto', cls: 'badge-warn' },
};
const CodeBadge = ({ v }) => {
  const s = CODE_STATUS[v] || { label: v, cls: 'badge-muted' };
  return <span className={`badge ${s.cls}`}>{s.label}</span>;
};
const CODE_ACCESSORS = {
  code: { get: (c) => c.code, type: 'string' },
  area_group: { get: (c) => c.area_group, type: 'string' },
  area_label: { get: (c) => c.area_label, type: 'string' },
  status: { get: (c) => c.status, type: 'string' },
  created_at: { get: (c) => c.created_at, type: 'date' },
};

function formatSize(bytes) {
  if (bytes == null) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let n = bytes, i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

const fmtDate = (v) => (v ? new Date(v).toLocaleString('it-IT') : '—');

// Stato di una segnalazione. 'open' = ancora da prendere in carico: e' cio' che
// conta il badge in sidebar e cio' che fa scattare la notifica all'amministratore.
const REPORT_STATUS = {
  open: { label: 'Aperta', cls: 'badge-warn' },
  closed: { label: 'Gestita', cls: 'badge-success' },
};
const statusOf = (r) => REPORT_STATUS[r.status] || { label: r.status, cls: 'badge-muted' };

// Riferimento tecnico mostrato in tabella: il dispositivo se c'e', altrimenti il
// codice attivazione (entrambi sono opzionali nel form di segnalazione).
const refOf = (r) => r.device_code || r.activation_code || '—';

function DashboardTecnico({ section, onBadges, user, supportVersion }) {
  const confirm = useConfirm();
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [searching, setSearching] = useState(false);

  // Elenco sfogliabile dei codici (in particolare quelli 'unused'): affianca
  // la ricerca puntuale sopra, per quando il tecnico non ha ancora un codice
  // preciso da verificare ma vuole vedere subito cosa c'e' di disponibile.
  const [codes, setCodes] = useState([]);
  const [codesLoading, setCodesLoading] = useState(false);
  const [codeSearch, setCodeSearch] = useState('');
  const [codeStatus, setCodeStatus] = useState('unused');
  const [codeColSort, onCodeSort] = useSort();

  const [installer, setInstaller] = useState(null);
  const [installerLoading, setInstallerLoading] = useState(false);
  // Versione caricata dallo sviluppatore ma non ancora approvata: le macchine
  // non la ricevono. Il tecnico la scarica a mano, la prova, e se va bene la
  // approva — da quel momento diventa l'aggiornamento distribuito.
  const [pending, setPending] = useState(null);

  // Contatore che rilancia il caricamento di installer e candidata dopo
  // un'approvazione (la candidata diventa la versione in distribuzione).
  const [installerReload, setInstallerReload] = useState(0);

  const [reportCode, setReportCode] = useState('');
  const [reportDevice, setReportDevice] = useState('');
  const [description, setDescription] = useState('');
  const [reportMsg, setReportMsg] = useState(null);
  const [createReportOpen, setCreateReportOpen] = useState(false); // drawer "Nuova segnalazione"

  // Elenco segnalazioni. L'amministratore (e il superadmin) legge quelle di
  // tutti e puo' prenderle in carico; il tecnico vede solo le proprie.
  const isAdmin = !!user && (user.role === 'amministratore' || user.role === 'superadmin');
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [repSearch, setRepSearch] = useState('');
  const [repStatus, setRepStatus] = useState('');
  const [detailId, setDetailId] = useState(null);
  const [busyId, setBusyId] = useState(null); // segnalazione in aggiornamento

  useEffect(() => {
    if (section !== 'installer') return;
    let active = true;
    setInstallerLoading(true);
    api('/api/tecnico/latest-installer')
      .then((res) => res.json())
      .then((data) => { if (active) setInstaller(data); })
      .catch(() => { if (active) setInstaller({ error: true }); })
      .finally(() => { if (active) setInstallerLoading(false); });
    api('/api/releases/pending-installer')
      .then((res) => res.json())
      .then((data) => { if (active) setPending(data); })
      .catch(() => { if (active) setPending(null); });
    return () => { active = false; };
  }, [section, installerReload]);

  useEffect(() => {
    if (section !== 'verifica') return;
    let active = true;
    setCodesLoading(true);
    api('/api/tecnico/activation-codes')
      .then((res) => res.json())
      .then((data) => { if (active) setCodes(Array.isArray(data) ? data : []); })
      .catch(() => { if (active) setCodes([]); })
      .finally(() => { if (active) setCodesLoading(false); });
    return () => { active = false; };
  }, [section]);

  const filteredCodes = useMemo(() => {
    const q = codeSearch.trim().toLowerCase();
    return codes.filter((c) => (!codeStatus || c.status === codeStatus)
      && (!q || [c.code, c.area_group, c.area_label].some((v) => (v || '').toLowerCase().includes(q))));
  }, [codes, codeSearch, codeStatus]);
  const sortedCodes = useMemo(() => sortRows(filteredCodes, codeColSort, CODE_ACCESSORS), [filteredCodes, codeColSort]);
  const codePagination = usePagination(sortedCodes.length, [codeSearch, codeStatus, codeColSort], 8);
  const pagedCodes = sortedCodes.slice(codePagination.start, codePagination.end);

  const loadReports = useCallback(async () => {
    setReportsLoading(true);
    try {
      const res = await api(isAdmin ? '/api/admin/support-reports' : '/api/tecnico/reports');
      if (!res.ok) throw new Error('load failed');
      const data = await res.json();
      setReports(data);
      // Il badge lo tiene aggiornato in tempo reale lo stream SSE (App.js), ma
      // riallinearlo anche qui rende il conteggio corretto pure se lo stream non
      // e' disponibile (browser senza EventSource, connessione caduta...).
      if (isAdmin && onBadges) {
        onBadges({ segnala: data.filter((r) => r.status === 'open').length });
      }
    } catch {
      setReports([]);
    } finally {
      setReportsLoading(false);
    }
  }, [isAdmin, onBadges]);

  // `supportVersion` cambia a ogni nuova segnalazione notificata via SSE (App.js):
  // se l'admin sta gia' guardando questa sezione, l'elenco si ricarica da solo.
  useEffect(() => {
    if (section !== 'segnala') return;
    loadReports();
  }, [section, loadReports, supportVersion]);

  // Link diretto, non fetch+blob: un download nativo del browser gira a
  // parte, con la sua UI di avanzamento — non blocca il pulsante ne'
  // impedisce di cambiare sezione mentre scarica (a differenza di un fetch
  // dentro questo componente, che si interrompe se lo si smonta navigando
  // altrove). In sviluppo locale (npm start) il proxy di CRA non inoltra al
  // backend una navigazione da link — non testabile con quel comando, ma
  // l'uso reale e' sempre sull'app pubblicata, dove non c'e' alcun proxy fra
  // frontend e backend: stesso vincolo gia' noto (vedi il commento
  // sull'export CSV qui sopra), qui si sceglie il lato opposto perche' non
  // bloccare la navigazione durante un file potenzialmente grande conta di
  // piu' che poterlo testare in locale.
  const downloadInstaller = () => {
    const a = document.createElement('a');
    a.href = '/api/tecnico/installer';
    a.download = (installer && installer.name) || 'HIPLAAS-Setup.exe';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // Scarica l'installer di una versione qualsiasi (qui: la candidata da
  // provare). Stesso approccio del download di sopra: link temporaneo, non
  // fetch, per non legare un file grande alla vita del componente.
  const downloadPendingInstaller = () => {
    if (!pending || !pending.available) return;
    const a = document.createElement('a');
    a.href = `/api/releases/${encodeURIComponent(pending.version)}/installer`;
    a.download = pending.name || 'HIPLAAS-Setup.exe';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleApprovePending = async () => {
    if (!pending || !pending.available) return;
    if (!(await confirm({
      title: 'Approva versione',
      message: `Approvare la versione ${pending.version}?\n\nDa quel momento è l'aggiornamento che tutte le macchine scaricano al controllo successivo. Approvala solo dopo averla provata.`,
      confirmLabel: 'Approva e distribuisci',
      icon: 'check',
    }))) return;
    const res = await api(`/api/releases/${encodeURIComponent(pending.version)}/approve`, { method: 'POST' });
    if (res.ok) { setInstallerReload((n) => n + 1); return; }
    const err = await res.json().catch(() => ({}));
    confirm({
      title: 'Approvazione non riuscita',
      message: err.error || 'Impossibile approvare la versione.',
      confirmLabel: 'Ho capito',
      hideCancel: true,
      icon: 'alert',
    });
  };
  const { run: runApprovePending, pending: approving } = useAsyncAction(handleApprovePending);

  const lookup = async (e) => {
    e.preventDefault();
    setResult(null);
    setSearching(true);
    try {
      const res = await api(`/api/tecnico/activation-codes/${encodeURIComponent(code.trim())}`);
      const data = await res.json();
      if (res.status === 404) { setResult({ notFound: true }); return; }
      setResult(data);
    } catch {
      setResult({ error: 'Errore di connessione' });
    } finally {
      setSearching(false);
    }
  };

  const submitReport = async (e) => {
    e.preventDefault();
    setReportMsg(null);
    const body = { description: description.trim() };
    if (reportCode.trim()) body.activation_code = reportCode.trim();
    if (reportDevice.trim()) body.device_code = reportDevice.trim();
    const res = await api('/api/tecnico/report-issue', { method: 'POST', body: JSON.stringify(body) });
    const data = await res.json();
    if (!res.ok) { setReportMsg({ type: 'error', text: data.error || 'Errore' }); return; }
    setReportMsg({ type: 'success', text: `Segnalazione #${data.id} inviata` });
    setReportCode(''); setReportDevice(''); setDescription('');
    loadReports(); // la nuova segnalazione compare subito nell'elenco qui sotto
  };
  const { run: runSubmitReport, pending: submittingReport } = useAsyncAction(submitReport);

  // Presa in carico / riapertura (solo amministratore).
  const setReportStatus = async (report, status) => {
    setReportMsg(null);
    setBusyId(report.id);
    try {
      const res = await api(`/api/admin/support-reports/${report.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (!res.ok) { setReportMsg({ type: 'error', text: data.error || 'Errore' }); return; }
      await loadReports();
    } catch {
      setReportMsg({ type: 'error', text: 'Errore di connessione' });
    } finally {
      setBusyId(null);
    }
  };

  const filteredReports = useMemo(() => {
    const q = repSearch.trim().toLowerCase();
    return reports.filter((r) => {
      if (repStatus && r.status !== repStatus) return false;
      if (!q) return true;
      return [r.reported_by, r.device_code, r.activation_code, r.description]
        .some((v) => (v || '').toLowerCase().includes(q));
    });
  }, [reports, repSearch, repStatus]);

  const repPagination = usePagination(filteredReports.length, [repSearch, repStatus, reports.length], 8);
  const pagedReports = filteredReports.slice(repPagination.start, repPagination.end);

  // Dettaglio derivato dall'id (non una copia): dopo una presa in carico il
  // drawer mostra subito il nuovo stato, e se la segnalazione sparisce si chiude.
  const detail = useMemo(() => reports.find((r) => r.id === detailId) || null, [reports, detailId]);

  if (section === 'verifica') {
    return (
      <>
        <div className="card">
          <div className="card-head"><h2><Icon name="search" size={18} />Verifica codice attivazione<HelpTip text="Usala subito dopo aver attivato una macchina, per confermare che sia andata a buon fine: se il codice risulta 'usato' vedi anche a quale dispositivo è collegato. Utile anche quando un cliente dice che 'non funziona' e vuoi capire se il suo codice è già stato usato, revocato o è scaduto." /></h2></div>
          <form className="inline-form" onSubmit={lookup}>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="vc">Codice</label>
              <input id="vc" className="input" placeholder="es. HIP-GR-LABO-2026-001" value={code} onChange={(e) => setCode(e.target.value)} required />
            </div>
            <button className="btn btn-primary" type="submit" disabled={searching || !code.trim()}>
              <Icon name="search" size={16} />{searching ? 'Verifico…' : 'Verifica'}
            </button>
          </form>
          {result && (
            <div style={{ marginTop: '1.25rem' }}>
              {result.notFound ? <div className="msg msg-error"><Icon name="alert" size={16} />Codice non trovato</div>
                : result.error ? <div className="msg msg-error"><Icon name="alert" size={16} />{result.error}</div>
                : (
                  <div className="table-wrap">
                    <table className="table">
                      <tbody>
                        <tr><th>Codice</th><td className="mono strong">{result.code}</td></tr>
                        <tr><th>Stato</th><td><CodeBadge v={result.status} /></td></tr>
                        <tr><th>Gruppo / Etichetta</th><td>{result.area_group} / {result.area_label}</td></tr>
                        {result.status === 'used' && <tr><th>Dispositivo</th><td className="mono strong">{result.device_code || '—'}</td></tr>}
                        {result.used_at && <tr><th>Usato il</th><td>{new Date(result.used_at).toLocaleString('it-IT')}</td></tr>}
                      </tbody>
                    </table>
                  </div>
                )}
            </div>
          )}
        </div>

        <div className="card" style={{ marginTop: '1.25rem' }}>
          <div className="card-head card-head--stacked">
            <h2><Icon name="key" size={18} />Codici attivazione<HelpTip text="Elenco di tutti i codici generati dall'amministratore. Filtra su 'Non usato' per trovare subito un codice libero da consegnare, senza doverlo già conoscere. Sola lettura: creazione e revoca restano nel pannello amministratore." /></h2>
            <div className="table-toolbar">
              <SearchInput id="tcCodeSearch" placeholder="Cerca codice, gruppo, etichetta…" value={codeSearch} onChange={(e) => setCodeSearch(e.target.value)} />
              <Select id="tcCodeStatus" className={codeStatus ? 'select-wrap--active' : ''} ariaLabel="Filtra per stato" value={codeStatus} onChange={(e) => setCodeStatus(e.target.value)} options={[
                { value: '', label: 'Tutti gli stati' }, { value: 'unused', label: 'Non usato' }, { value: 'used', label: 'Usato' }, { value: 'revoked', label: 'Revocato' }, { value: 'expired', label: 'Scaduto' },
              ]} />
            </div>
          </div>
          {codesLoading ? (
            <div className="empty">Caricamento…</div>
          ) : codes.length === 0 ? (
            <div className="empty"><Icon name="key" size={30} /><span>Nessun codice creato</span></div>
          ) : filteredCodes.length === 0 ? (
            <div className="empty"><Icon name="search" size={30} /><span>Nessun risultato per i filtri applicati</span></div>
          ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <SortableTh label="Codice" sortKey="code" sort={codeColSort} onSort={onCodeSort} style={{ width: '30%' }} />
                      <SortableTh label="Gruppo" sortKey="area_group" sort={codeColSort} onSort={onCodeSort} style={{ width: '14%' }} />
                      <SortableTh label="Etichetta" sortKey="area_label" sort={codeColSort} onSort={onCodeSort} style={{ width: '22%' }} />
                      <SortableTh label="Stato" sortKey="status" sort={codeColSort} onSort={onCodeSort} style={{ width: '16%' }} />
                      <SortableTh label="Creato" sortKey="created_at" sort={codeColSort} onSort={onCodeSort} style={{ width: '18%' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {pagedCodes.map((c) => (
                      <tr key={c.id}>
                        <td className="mono strong">{c.code}</td>
                        <td>{c.area_group}</td><td>{c.area_label}</td>
                        <td><CodeBadge v={c.status} /></td>
                        <td>{fmtDate(c.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
          )}
          <Pagination idPrefix="tcCodePage" total={sortedCodes.length} {...codePagination} onPageChange={codePagination.setPage} onPageSizeChange={codePagination.setPageSize} showSizeSelector={false} />
        </div>
      </>
    );
  }

  if (section === 'installer') {
    return (
      <div className="card">
        <div className="card-head"><h2><Icon name="download" size={18} />Scarica installer di partenza<HelpTip text="Ti serve prima di andare sul posto o durante l'installazione: scarichi l'ultimo installer per preparare una macchina nuova, anche se non è ancora attivata (non richiede una licenza)." /></h2></div>
        <p className="hint" style={{ marginTop: 0 }}>
          Installer HIPLAAS più recente, da usare per configurare un nuovo dispositivo prima dell'attivazione.
        </p>
        {installerLoading ? (
          <div className="empty">Caricamento…</div>
        ) : !installer || installer.error ? (
          <div className="msg msg-error"><Icon name="alert" size={16} />Impossibile recuperare l'installer</div>
        ) : !installer.available ? (
          <div className="empty">Nessun installer pubblicato</div>
        ) : (
          <>
            <div className="table-wrap" style={{ marginBottom: '1.25rem' }}>
              <table className="table">
                <tbody>
                  <tr><th>File</th><td className="mono strong">{installer.name}</td></tr>
                  <tr><th>Versione</th><td><span className="badge badge-info">{installer.version}</span></td></tr>
                  <tr><th>Dimensione</th><td>{formatSize(installer.size)}</td></tr>
                  {installer.uploaded_at && <tr><th>Pubblicato il</th><td>{new Date(installer.uploaded_at).toLocaleString('it-IT')}</td></tr>}
                </tbody>
              </table>
            </div>
            <button className="btn btn-primary" type="button" onClick={downloadInstaller}>
              <Icon name="download" size={16} />Scarica installer
            </button>
          </>
        )}
        {pending && pending.available && (
          <div className="card" style={{ marginTop: '1.5rem' }}>
            <div className="card-head">
              <h2>
                <Icon name="package" size={18} />Versione da testare
                <HelpTip text="Una versione appena rilasciata non arriva alle macchine: resta «Da testare». Scaricala, provala su una macchina di collaudo e, solo se tutto funziona, approvala: da quel momento diventa l'aggiornamento che tutte le macchine scaricano." />
              </h2>
            </div>
            <p className="hint" style={{ marginTop: 0 }}>
              La versione <strong>{pending.version}</strong> è stata rilasciata ma non è ancora distribuita. Provala e poi approvala.
            </p>
            <div className="table-wrap" style={{ marginBottom: '1.25rem' }}>
              <table className="table">
                <tbody>
                  <tr><th>File</th><td className="mono strong">{pending.name}</td></tr>
                  <tr><th>Versione</th><td><span className="badge badge-warn">{pending.version} — Da testare</span></td></tr>
                  <tr><th>Dimensione</th><td>{formatSize(pending.size)}</td></tr>
                  {pending.uploaded_at && <tr><th>Caricata il</th><td>{new Date(pending.uploaded_at).toLocaleString('it-IT')}</td></tr>}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button className="btn btn-primary" type="button" onClick={downloadPendingInstaller}>
                <Icon name="download" size={16} />Scarica per provarla
              </button>
              <button className="btn btn-accent" type="button" disabled={approving} onClick={runApprovePending}>
                <Icon name="check" size={16} />{approving ? 'Approvazione…' : 'Approva e distribuisci'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (section === 'segnala') {
    const openCreateReport = () => { setReportMsg(null); setCreateReportOpen(true); };
    return (
      <>
        <div className="card">
          <div className="card-head card-head--stacked">
            <h2>
              <Icon name="activity" size={18} />
              {isAdmin ? 'Segnalazioni ricevute' : 'Le mie segnalazioni'}
              <HelpTip text={isAdmin
                ? "Tutte le segnalazioni aperte dai tecnici. Clicca una riga per leggere il testo completo e segnarla come gestita: le segnalazioni ancora aperte sono quelle contate dal badge qui a sinistra."
                : "Lo storico delle segnalazioni che hai inviato. Restano 'Aperte' finche' l'amministratore non le prende in carico."} />
            </h2>
            <div className="table-toolbar">
              {isAdmin && (
                <>
                  <SearchInput id="repSearch" placeholder="Cerca tecnico, dispositivo, testo…" value={repSearch} onChange={(e) => setRepSearch(e.target.value)} />
                  <Select
                    id="repStatus"
                    className={repStatus ? 'select-wrap--active' : ''}
                    ariaLabel="Filtra per stato"
                    value={repStatus}
                    onChange={(e) => setRepStatus(e.target.value)}
                    options={[
                      { value: '', label: 'Tutti gli stati' },
                      { value: 'open', label: 'Da gestire' },
                      { value: 'closed', label: 'Gestite' },
                    ]}
                  />
                </>
              )}
              <button type="button" className="btn btn-accent" style={{ marginLeft: 'auto' }} onClick={openCreateReport}>
                <Icon name="alertMsg" size={16} />Nuova segnalazione
              </button>
            </div>
          </div>

          {reportsLoading ? (
            <div className="empty">Caricamento…</div>
          ) : reports.length === 0 ? (
            <div className="empty">
              <Icon name="alertMsg" size={30} />
              <span>{isAdmin ? 'Nessuna segnalazione ricevuta' : 'Non hai ancora inviato segnalazioni'}</span>
            </div>
          ) : filteredReports.length === 0 ? (
            <div className="empty"><Icon name="search" size={30} /><span>Nessun risultato per i filtri applicati</span></div>
          ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ width: isAdmin ? '16%' : '20%' }}>Data</th>
                      {isAdmin && <th style={{ width: '14%' }}>Tecnico</th>}
                      <th style={{ width: isAdmin ? '16%' : '20%' }}>Riferimento</th>
                      <th style={{ width: isAdmin ? '34%' : '43%' }}>Descrizione</th>
                      <th style={{ width: isAdmin ? '15%' : '12%' }}>Stato</th>
                      <th style={{ width: '5%' }} aria-label="Dettaglio"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedReports.map((r) => {
                      const st = statusOf(r);
                      return (
                        <tr key={r.id} className="report-row" onClick={() => setDetailId(r.id)}>
                          <td className="audit-when">{fmtDate(r.created_at)}</td>
                          {isAdmin && <td className="mono strong">{r.reported_by}</td>}
                          <td className="mono">{refOf(r)}</td>
                          <td className="report-desc">{r.description}</td>
                          <td><span className={`badge ${st.cls}`}>{st.label}</span></td>
                          <td className="report-row-open" aria-label="Apri dettaglio"><Icon name="chevronRight" size={15} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
          )}
          <Pagination
            idPrefix="repPage"
            total={filteredReports.length}
            {...repPagination}
            onPageChange={repPagination.setPage}
            onPageSizeChange={repPagination.setPageSize}
            itemLabel="segnalazioni"
            showSizeSelector={false}
          />
        </div>

        {createReportOpen && (
          <Drawer title="Nuova segnalazione" icon="alertMsg" onClose={() => setCreateReportOpen(false)}>
            <p className="hint" style={{ marginTop: 0 }}>
              Usala quando durante l'installazione o l'attivazione qualcosa non va (codice rifiutato, hardware non rilevato…): indica codice o dispositivo e cosa è successo.
            </p>
            <form className="drawer-form" onSubmit={runSubmitReport}>
              <div className="field">
                <label htmlFor="rc">Codice attivazione (opzionale)</label>
                <input id="rc" className="input" placeholder="HIP-…" value={reportCode} onChange={(e) => setReportCode(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="rd">Device code (opzionale)</label>
                <input id="rd" className="input" placeholder="HIP-00001" value={reportDevice} onChange={(e) => setReportDevice(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="desc">Descrizione del problema</label>
                <textarea id="desc" className="textarea" placeholder="Descrivi il problema riscontrato…" value={description} onChange={(e) => setDescription(e.target.value)} rows={4} required />
              </div>
              <button className="btn btn-accent" type="submit" disabled={submittingReport || !description.trim()}><Icon name="alertMsg" size={16} />{submittingReport ? 'Invio…' : 'Invia segnalazione'}</button>
            </form>
            {reportMsg && <div className={`msg msg-${reportMsg.type}`} style={{ marginTop: '1rem' }}><Icon name={reportMsg.type === 'success' ? 'check' : 'alert'} size={16} />{reportMsg.text}</div>}
          </Drawer>
        )}

        {detail && (
          <Drawer title={`Segnalazione #${detail.id}`} icon="alertMsg" onClose={() => setDetailId(null)}>
            <div className="audit-detail-panel">
              <div className="audit-detail-title">
                <span className="strong mono">{refOf(detail)}</span>
                <span className={`badge ${statusOf(detail).cls}`}>{statusOf(detail).label}</span>
              </div>
              <div className="audit-detail-block">
                <span className="audit-detail-k">Da</span>
                <span className="audit-detail-v">{detail.reported_by} <span className="audit-detail-sub">({detail.reporter_role})</span></span>
              </div>
              <div className="audit-detail-block">
                <span className="audit-detail-k">Data</span>
                <span className="audit-detail-v">{fmtDate(detail.created_at)}</span>
              </div>
              <div className="audit-detail-block">
                <span className="audit-detail-k">Device</span>
                <span className="audit-detail-v mono">{detail.device_code || '—'}</span>
              </div>
              <div className="audit-detail-block">
                <span className="audit-detail-k">Codice</span>
                <span className="audit-detail-v mono">{detail.activation_code || '—'}</span>
              </div>
              <div className="audit-detail-block">
                <span className="audit-detail-k">Problema</span>
                <span className="audit-detail-v report-detail-text">{detail.description}</span>
              </div>
              {detail.status === 'closed' && (
                <div className="audit-detail-block">
                  <span className="audit-detail-k">Gestita</span>
                  <span className="audit-detail-v">
                    {detail.handled_by || '—'}
                    {detail.handled_at && <span className="audit-detail-sub"> — {fmtDate(detail.handled_at)}</span>}
                  </span>
                </div>
              )}
              {isAdmin && (
                <div className="audit-detail-actions">
                  {detail.status === 'open' ? (
                    <button type="button" className="btn btn-primary btn-block" disabled={busyId === detail.id} onClick={() => setReportStatus(detail, 'closed')}>
                      <Icon name="check" size={14} />{busyId === detail.id ? 'Aggiorno…' : 'Segna come gestita'}
                    </button>
                  ) : (
                    <button type="button" className="btn btn-warn btn-block" disabled={busyId === detail.id} onClick={() => setReportStatus(detail, 'open')}>
                      <Icon name="refresh" size={14} />{busyId === detail.id ? 'Aggiorno…' : 'Riapri segnalazione'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </Drawer>
        )}
      </>
    );
  }

  return null;
}

export default DashboardTecnico;
